import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { isPlausibleBirthDate } from "../utils/date.utils";
import { isAcceptablePhoneNumber } from "../utils/phone.utils";
import {
  CivilityEnum,
  NotificationPrefEnum,
  RoleEnum,
} from "../types/user.definition";
import { optionalLocationsArraySchema } from "./location.schema";

/** @param existingPhone the stored number, kept as is it does not need the stricter rule (MED-1653). */
export const profileSchema = (t: TFunction, existingPhone?: string) =>
  z.object({
    firstName: z.string().min(1, t("common.validation.required")),
    roleName: z.enum([RoleEnum.MANAGER, RoleEnum.SECRETARY], {
      message: t("common.validation.required"),
    }),
    lastName: z.string().min(1, t("common.validation.required")),
    email: z.string().email(t("common.validation.invalidEmail")),
    phoneNumber: z.string().min(1, t("common.validation.required")).refine(
      (val) => isAcceptablePhoneNumber(val, existingPhone),
      { message: t("common.validation.invalidPhoneNumber") }
    ),
    civility: z.enum([CivilityEnum.MR, CivilityEnum.MRS], {
      message: t("common.validation.required"),
    }),
    preferredNotificationMethods: z.array(
      z.enum([NotificationPrefEnum.EMAIL, NotificationPrefEnum.SMS]),
      {
        message: t("common.validation.required"),
      }
    ),
    // MED-1653: optional, but a typed date must be plausible ("51519-09-01" was accepted).
    dateOfBirth: z
      .string()
      .optional()
      .refine((date) => !date || isPlausibleBirthDate(date), {
        message: t("common.validation.invalidDateOfBirth"),
      }),
    photoUrl: z.string().optional(),
    locations: optionalLocationsArraySchema(t),
  });

export const useProfileSchema = (existingPhone?: string) => {
  const { t } = useTranslation();
  return profileSchema(t, existingPhone);
};

export type ProfileSchema = z.infer<ReturnType<typeof useProfileSchema>>;
